import React, { useEffect, useState, useCallback } from 'react';
import "../style.scss";
import { managerDashboardService } from "../../../services/managerDashboardService";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Container,
  ButtonGroup,
  Button,
  Table,
} from 'reactstrap';
import { HorizontalBar, Doughnut } from 'react-chartjs-2';

const colors = {
  gray: {
    100: "#f6f9fc",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#8898aa",
    700: "#525f7f",
    800: "#172430",
    900: "#212529",
  },
};

const CHART_COLORS = [
  '#C2641A', '#172430', '#046BD2', '#61CE70',
  '#D9534F', '#fb6340', '#8965e0', '#2dce89',
  '#f5365c', '#5e72e4', '#11cdef', '#ffd600',
];

const MEDAL_STYLES = [
  { bg: 'rgba(255, 215, 0, 0.12)', border: '#FFD700', label: '1o' },
  { bg: 'rgba(192, 192, 192, 0.12)', border: '#C0C0C0', label: '2o' },
  { bg: 'rgba(205, 127, 50, 0.12)', border: '#CD7F32', label: '3o' },
];

const fmt = (v) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtCompact = (v) => {
  const n = Math.abs(v || 0);
  if (n >= 1e9) return `R$ ${(v / 1e9).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} bi`;
  if (n >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  return fmt(v);
};

const fmtNum = (v) => (v || 0).toLocaleString('pt-BR');

// ─── Summary Cards ──────────────────────────────────────────────
const SummaryCards = ({ data }) => {
  if (!data) return null;
  const cards = [
    { label: 'Total de Processos', value: fmtNum(data.total_processes), title: fmtNum(data.total_processes), bg: '#172430', color: '#fff', icon: 'ni ni-archive-2' },
    { label: 'Valor Total', value: fmtCompact(data.total_value), title: fmt(data.total_value), bg: '#C2641A', color: '#fff', icon: 'ni ni-money-coins' },
    { label: 'Valor Negociado', value: fmtCompact(data.total_negotiated_value), title: fmt(data.total_negotiated_value), bg: '#046BD2', color: '#fff', icon: 'ni ni-chart-bar-32' },
    { label: 'Honorarios', value: fmtCompact(data.total_honorary_value), title: fmt(data.total_honorary_value), bg: '#61CE70', color: '#fff', icon: 'ni ni-briefcase-24' },
    { label: 'Atribuidos', value: fmtNum(data.processes_assigned), title: fmtNum(data.processes_assigned), bg: '#fff', color: '#172430', icon: 'ni ni-single-02' },
    { label: 'Nao Atribuidos', value: fmtNum(data.processes_unassigned), title: fmtNum(data.processes_unassigned), bg: '#fff', color: '#D9534F', icon: 'ni ni-fat-remove' },
  ];

  return (
    <Row className="mb-4">
      {cards.map((c, i) => (
        <Col lg="2" md="4" sm="6" className="mb-3" key={i}>
          <Card className="shadow-sm border-0 h-100" style={{ backgroundColor: c.bg, color: c.color }}>
            <CardBody className="py-3 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="mb-0 small font-weight-600" style={{ opacity: 0.8 }}>{c.label}</p>
                  <h3 className="mb-0 font-weight-700" style={{ color: c.color }} title={c.title}>{c.value}</h3>
                </div>
                <i className={`${c.icon}`} style={{ fontSize: '1.5rem', opacity: 0.6 }} />
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// ─── Top Performers ─────────────────────────────────────────────
const TopPerformers = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="ni ni-trophy display-4 opacity-5 mb-2" />
        <p>Sem atividade no periodo</p>
      </div>
    );
  }

  return (
    <Row>
      {data.map((user, idx) => {
        const medal = MEDAL_STYLES[idx] || MEDAL_STYLES[2];
        return (
          <Col md="4" key={user.user_id} className="mb-3">
            <Card
              className="shadow-sm border-0 h-100"
              style={{ backgroundColor: medal.bg, borderLeft: `4px solid ${medal.border}` }}
              data-testid={`top-performer-${idx}`}
            >
              <CardBody className="py-3">
                <div className="d-flex align-items-center mb-2">
                  <span className="font-weight-700 mr-2" style={{ fontSize: '1.2rem', color: medal.border }}>
                    {medal.label}
                  </span>
                  <h4 className="mb-0 font-weight-700 text-dark">
                    {user.first_name} {user.last_name}
                  </h4>
                </div>
                <p className="mb-0 small text-muted">
                  <span className="font-weight-600">{user.activity_score}</span> pts
                  {' | '}
                  {fmt(user.status_changes_value)}
                </p>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

// ─── Activity Chart (Horizontal Bar) ────────────────────────────
const ActivityChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex w-100 flex-column justify-content-center align-items-center text-muted py-5">
        <i className="ni ni-chart-bar-32 display-4 opacity-5 mb-3" />
        <p>Sem atividade no periodo</p>
      </div>
    );
  }

  const chartData = {
    labels: data.map(u => `${u.first_name} ${u.last_name}`),
    datasets: [{
      label: 'Score de Atividade',
      data: data.map(u => u.activity_score),
      backgroundColor: 'rgba(194, 100, 26, 0.85)',
      borderColor: 'rgba(194, 100, 26, 1)',
      borderWidth: 1,
    }],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      xAxes: [{
        ticks: { beginAtZero: true, precision: 0, fontColor: colors.gray[600] },
        gridLines: { color: colors.gray[200], drawBorder: false },
      }],
      yAxes: [{
        ticks: { fontColor: colors.gray[600] },
        gridLines: { display: false },
      }],
    },
  };

  return (
    <div style={{ position: 'relative', height: Math.max(200, data.length * 45), width: '100%' }}>
      <HorizontalBar data={chartData} options={options} />
    </div>
  );
};

// ─── Status Distribution (Doughnut) ─────────────────────────────
const StatusDistributionChart = ({ data }) => {
  const activeData = data ? data.filter(s => s.quantity > 0) : [];

  if (activeData.length === 0) {
    return (
      <div className="d-flex w-100 flex-column justify-content-center align-items-center text-muted py-5">
        <i className="ni ni-chart-pie-35 display-4 opacity-5 mb-3" />
        <p>Sem dados de status</p>
      </div>
    );
  }

  const chartData = {
    labels: activeData.map(s => s.name),
    datasets: [{
      data: activeData.map(s => s.quantity),
      backgroundColor: CHART_COLORS.slice(0, activeData.length),
      borderWidth: 1,
    }],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    legend: { position: 'right', labels: { fontColor: colors.gray[700] } },
    tooltips: {
      callbacks: {
        label: function (item, data) {
          const label = data.labels[item.index] || '';
          const value = data.datasets[0].data[item.index] || 0;
          return ` ${label}: ${fmtNum(value)}`;
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', height: '300px', width: '100%' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

// ─── Team Ranking Table ─────────────────────────────────────────
const TeamRankingTable = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <Table responsive className="align-items-center table-flush" size="sm">
      <thead className="thead-light">
        <tr>
          <th>Colaborador</th>
          <th className="text-right">Processos</th>
          <th className="text-right">Valor Total</th>
          <th className="text-right">Valor Negociado</th>
          <th className="text-right">Sem Status</th>
        </tr>
      </thead>
      <tbody>
        {data.map((u) => (
          <tr key={u.user_id}>
            <td className="font-weight-600">{u.first_name} {u.last_name}</td>
            <td className="text-right">{fmtNum(u.total_processes)}</td>
            <td className="text-right">{fmt(u.total_value)}</td>
            <td className="text-right">{fmt(u.total_negotiated_value)}</td>
            <td className="text-right">
              {u.processes_without_status > 0 ? (
                <span className="text-danger font-weight-600">{u.processes_without_status}</span>
              ) : (
                <span className="text-success">0</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// ─── Main Component ─────────────────────────────────────────────
const ManagerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('weekly');

  const fetchData = useCallback(async () => {
    setLoader(true);
    setError(null);
    try {
      const response = await managerDashboardService.fetchStats();
      setStats(response.data);
    } catch (err) {
      console.error(err);
      setError('Ocorreu um erro ao carregar os dados do painel gerencial.');
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activityData = stats
    ? (period === 'weekly' ? stats.weekly_activity : stats.monthly_activity)
    : null;
  const topData = stats
    ? (period === 'weekly' ? stats.top_performers.weekly : stats.top_performers.monthly)
    : null;

  return (
    <>
      {error && (
        <Alert color="danger" style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10000, minWidth: '280px' }}>
          {error}
        </Alert>
      )}

      <div className="content">
        <Container fluid>
          {/* Header */}
          <Row className="mb-4 align-items-center">
            <Col>
              <h2 className="mb-0 text-dark" style={{ fontWeight: "700" }}>
                <i className="ni ni-chart-bar-32 text-primary mr-2" />
                Painel Gerencial
              </h2>
              <p className="text-muted mb-0 small">
                Visao consolidada de performance da equipe
              </p>
            </Col>
            <Col xs="auto">
              <ButtonGroup>
                <Button
                  color={period === 'weekly' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setPeriod('weekly')}
                  data-testid="btn-weekly"
                >
                  Semanal
                </Button>
                <Button
                  color={period === 'monthly' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setPeriod('monthly')}
                  data-testid="btn-monthly"
                >
                  Mensal
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          {loader ? (
            <div className="text-center py-5">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: '3rem', height: '3rem' }}
              >
                <span className="sr-only">Carregando...</span>
              </div>
              <p className="text-muted mt-3">Carregando painel gerencial...</p>
            </div>
          ) : stats && (
            <>
              {/* Summary Cards */}
              <SummaryCards data={stats.global_summary} />

              {/* Top Performers */}
              <Card className="shadow-sm border-0 mb-4">
                <CardHeader className="bg-transparent border-0">
                  <h4 className="mb-0 text-uppercase text-muted ls-1 small font-weight-bold">
                    Destaque {period === 'weekly' ? 'da Semana' : 'do Mes'}
                  </h4>
                  <h3 className="mb-0 font-weight-700">Top 3 Colaboradores</h3>
                </CardHeader>
                <CardBody>
                  <TopPerformers data={topData} />
                </CardBody>
              </Card>

              {/* Activity Chart + Status Distribution */}
              <Row>
                <Col lg="7">
                  <Card className="shadow-sm border-0 mb-4">
                    <CardHeader className="bg-transparent border-0">
                      <h4 className="mb-0 text-uppercase text-muted ls-1 small font-weight-bold">
                        Atividade {period === 'weekly' ? 'Semanal' : 'Mensal'}
                      </h4>
                      <h3 className="mb-0 font-weight-700">Score por Colaborador</h3>
                      <p className="mb-0 mt-1 small text-muted">
                        Pontuação = mudanças de status ×2 + notas + telefones + arquivos
                      </p>
                    </CardHeader>
                    <CardBody>
                      <ActivityChart data={activityData?.per_user} />
                    </CardBody>
                  </Card>
                </Col>
                <Col lg="5">
                  <Card className="shadow-sm border-0 mb-4">
                    <CardHeader className="bg-transparent border-0">
                      <h4 className="mb-0 text-uppercase text-muted ls-1 small font-weight-bold">
                        Distribuicao Global
                      </h4>
                      <h3 className="mb-0 font-weight-700">Status dos Processos</h3>
                    </CardHeader>
                    <CardBody>
                      <StatusDistributionChart data={stats.status_distribution} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Team Ranking Table */}
              <Card className="shadow-sm border-0 mb-4">
                <CardHeader className="bg-transparent border-0">
                  <h4 className="mb-0 text-uppercase text-muted ls-1 small font-weight-bold">
                    Ranking da Equipe
                  </h4>
                  <h3 className="mb-0 font-weight-700">Detalhamento por Colaborador</h3>
                </CardHeader>
                <CardBody className="p-0">
                  <TeamRankingTable data={stats.per_user_stats} />
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default ManagerDashboard;
